import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import "./CountdownBanner.css";

const DEFAULT_BANNER = {
  enabled: false,
  label: "MUNDIAL 2026",
  title: "CONTEO REGRESIVO PARA",
  accent: "LA FIESTA",
  subtitle: "DEL FÚTBOL",
  image_url: "",
  bg_color: "#0a1535",
  text_color: "#ffffff",
  accent_color: "#1e6bff",
  link: "/categoria/deportes",
  start_date: "",
  end_date: "",
  next_banner: null,
};

function useCountdown(targetIso) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!targetIso) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  if (!targetIso) return null;
  const diff = new Date(targetIso).getTime() - Date.now();
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, finished: true };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, finished: false };
}

function NumberCell({ value, label }) {
  return (
    <div className="cd-cell" data-testid={`countdown-cell-${label.toLowerCase()}`}>
      <div className="cd-num">{String(value).padStart(2, "0")}</div>
      <div className="cd-label">{label}</div>
    </div>
  );
}

function CountdownArt({ banner, cd }) {
  const cssVars = {
    "--cd-bg": banner.bg_color || "#0a1535",
    "--cd-accent": banner.accent_color || "#1e6bff",
    "--cd-text": banner.text_color || "#ffffff",
  };
  const labelWords = (banner.label || "").split(/\s+/).filter(Boolean);
  const hasImage = !!banner.image_url;
  // If image is set: hide overlay text by default unless admin enabled `show_overlay_text`
  const showText = !hasImage || !!banner.show_overlay_text;

  return (
    <div className={`cd-root ${hasImage ? "cd-with-image" : ""} ${hasImage && showText ? "cd-with-image-text" : ""}`} style={cssVars} data-testid="countdown-banner">
      {hasImage ? (
        <div className="cd-bg-image-full" style={{ backgroundImage: `url(${banner.image_url})` }} />
      ) : (
        <>
          <div className="cd-bg-base" />
          <div className="cd-bg-rays" />
          <div className="cd-bg-grid" />
          <div className="cd-bg-orb" />
        </>
      )}

      <div className="cd-inner">
        {showText && labelWords.length > 0 && (
          <div className="cd-left">
            <div className="cd-left-words">
              {labelWords.map((w, i) => (
                <span key={i} className="cd-left-word">{w}</span>
              ))}
            </div>
            <div className="cd-left-bar" />
          </div>
        )}

        <div className="cd-center">
          <NumberCell value={cd?.d ?? 0} label="DÍAS" />
          <div className="cd-divider" />
          <NumberCell value={cd?.h ?? 0} label="HORAS" />
          <div className="cd-divider" />
          <NumberCell value={cd?.m ?? 0} label="MINUTOS" />
          <div className="cd-divider" />
          <NumberCell value={cd?.s ?? 0} label="SEGUNDOS" />
        </div>

        {showText && (banner.title || banner.accent || banner.subtitle) && (
          <div className="cd-right">
            {banner.title && <p className="cd-right-title">{banner.title}</p>}
            {banner.accent && <p className="cd-right-accent">{banner.accent}</p>}
            {banner.subtitle && <p className="cd-right-subtitle">{banner.subtitle}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function BannerCard({ banner }) {
  const cd = useCountdown(banner.end_date);
  if (!banner.enabled || !banner.end_date) return null;
  if (banner.start_date && new Date(banner.start_date).getTime() > Date.now()) return null;
  if (cd?.finished) {
    const nb = banner.next_banner;
    if (nb && nb.enabled) return <NextBanner banner={{ ...DEFAULT_BANNER, ...nb }} />;
    return null;
  }
  const art = <CountdownArt banner={banner} cd={cd} />;
  return banner.link ? <Link to={banner.link} className="block">{art}</Link> : art;
}

function NextBannerCountdown({ banner }) {
  const cd = useCountdown(banner.end_date);
  if (cd?.finished) return null;
  if (banner.start_date && new Date(banner.start_date).getTime() > Date.now()) return null;
  const art = <CountdownArt banner={banner} cd={cd} />;
  return banner.link ? <Link to={banner.link} className="block">{art}</Link> : art;
}

function NextBanner({ banner }) {
  if (banner.end_date && new Date(banner.end_date).getTime() < Date.now()) return null;
  if (banner.start_date && new Date(banner.start_date).getTime() > Date.now()) return null;

  if (banner.end_date) {
    return <NextBannerCountdown banner={banner} />;
  }

  const cssVars = {
    "--cd-bg": banner.bg_color || "#0a1535",
    "--cd-accent": banner.accent_color || "#1e6bff",
    "--cd-text": banner.text_color || "#ffffff",
  };

  const content = (
    <div className="cd-root cd-next" style={cssVars} data-testid="countdown-next-banner">
      <div className="cd-bg-base" />
      {banner.image_url && (
        <div className="cd-next-image" style={{ backgroundImage: `url(${banner.image_url})` }} />
      )}
      <div className="cd-next-inner">
        {banner.label && <p className="cd-next-label">{banner.label}</p>}
        <p className="cd-next-title">
          {banner.title || "¡Comienza!"} {banner.accent && <span className="cd-next-accent-word">{banner.accent}</span>} {banner.subtitle}
        </p>
      </div>
    </div>
  );
  return banner.link ? <Link to={banner.link} className="block">{content}</Link> : content;
}

export default function CountdownBanner() {
  const [banner, setBanner] = useState(null);
  useEffect(() => {
    api
      .get("/settings/public")
      .then((r) => setBanner(r.data?.countdown_banner || null))
      .catch(() => {});
  }, []);
  if (!banner || !banner.enabled) return null;
  return <BannerCard banner={{ ...DEFAULT_BANNER, ...banner }} />;
}
